import R from 'app/assets/R'

import { updateBill } from 'app/api/accounting'
import { AccountingMessageCode } from 'app/api/accounting/constant'
import {
  EBillStatus,
  IFilterGetExportBill,
  IGetExportBill,
  IPayloadUpdateExportBill,
} from 'app/api/accounting/model/export-bill'
import { getDetailPatientUrl } from 'app/common/helpers'
import {
  FunctionPermissionEnum,
  ModulePermissionEnum,
} from 'app/common/permission-module'
import { useVerifyPermissionAndRedirect } from 'app/common/use-verify-permission-redirect'
import { BaseCol } from 'app/components/common/BaseCol'
import { RenderValueTable } from 'app/components/tables/BaseTableManagement/hook'
import { convertColumnTable } from 'app/components/tables/BaseTableReport/hook'
import { initPagination } from 'app/constant'
import { notificationController } from 'app/controllers/notification-controller'
import { useDebounce, usePagination } from 'app/hook'
import { useGetDataExportBill } from 'app/react-query/hook/accounting'
import { isEqual, uniq, uniqBy } from 'lodash'
import { DateUtil, Pagination, formatMoney, moment } from 'parkway-web-common'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CheckBox,
  CheckBoxContainer,
  ListItemText,
  TableText,
} from '../../styles'
import { IDataTable } from '../../type'

export const useTable = ({
  reloadTable,
  setReloadTable,
}: {
  reloadTable: number
  setReloadTable: React.Dispatch<React.SetStateAction<number>>
}) => {
  const { t } = useTranslation()
  const [pagination, setPagination] = useState(initPagination)

  const [isLoading, setIsLoading] = useState(false)
  const [filter, setFilter] = useState<IFilterGetExportBill>({})

  const { flattenDataList } = usePagination()

  const filterDebounce = useDebounce<IFilterGetExportBill>(filter, 500)

  const { isHavePermissionFunction } = useVerifyPermissionAndRedirect()

  const editPermission = isHavePermissionFunction(
    ModulePermissionEnum.ACCOUNTING,
    FunctionPermissionEnum.EDIT_DATA_REQUEST_EXPORT_BILL,
  )

  const {
    data: dataInfinity,
    isLoading: isLoadingTable,
    refetch: refetchData,
  } = useGetDataExportBill({
    keyword: filterDebounce?.keyword ?? '',
    page: pagination.current,
    pageSize: pagination.pageSize,
    pagesize: pagination.pageSize,
    fromDate: filterDebounce?.fromDate,
    toDate: filterDebounce?.toDate,
    clinicIds: filterDebounce?.clinicIds,
    clinicLegalNameIds: filterDebounce?.clinicLegalNameIds,
    status: EBillStatus?.COMPLETED,
  })

  const dataFlatten = useMemo(() => {
    const res = flattenDataList(dataInfinity)
    return res
  }, [dataInfinity])

  const data = useMemo(() => {
    return dataFlatten?.data?.map((item, index) => {
      return {
        ...item,
        key: index + 1,
      } as IDataTable
    })
  }, [dataFlatten])

  const totalItem = useMemo(() => {
    return dataFlatten?.data?.total ?? 0
  }, [dataFlatten])

  const updateCheckBox = async ({
    id,
    body,
  }: {
    id: string
    body: IPayloadUpdateExportBill
  }) => {
    setIsLoading(true)
    try {
      const response = await updateBill({ id, body })

      if (
        isEqual(
          response?.msgcode,
          AccountingMessageCode.ExportBill.createSuccess,
        )
      ) {
        setIsLoading(false)
        notificationController.success({
          message: t(R.strings.update_successfully),
        })
        setReloadTable(prev => prev + 1)
      } else {
        notificationController.error({
          message: t(R.strings.error),
          description: response?.message,
        })
      }
    } catch (error) {
      setIsLoading(false)
    } finally {
      setIsLoading(false)
    }
  }

  const columns = useMemo(() => {
    const option = [
      convertColumnTable<IDataTable>({
        title: t(R.strings.request_export_bill_date),
        key: 'request_export_bill_date',
        className: 'export-bill-date-column',
        render: (_, record: IGetExportBill) => {
          return (
            <TableText
              children={
                record?.createdAt
                  ? moment(record?.createdAt).format('HH:mm DD/MM/YYYY')
                  : '-'
              }
              fontWeight="medium"
              opacity="0.9"
              textAlign="center"
            />
          )
        },
      }),
      convertColumnTable<IDataTable>({
        title: t(R.strings.payment_code),
        key: 'payment_code',
        className: 'payment-code-column',
        render: (_, record: IGetExportBill) => {
          return (
            <BaseCol>
              {record?.paymentDetails?.map((i, index) => (
                <ListItemText
                  $marginTop={index !== 0 ? 8 : 0}
                  key={`${index}`}
                  children={`${i?.code}`}
                  fontWeight="medium"
                  opacity="0.9"
                  textAlign="center"
                />
              ))}
            </BaseCol>
          )
        },
      }),
      {
        title: t(R.strings.customer_info),
        children: [
          convertColumnTable<IDataTable>({
            title: t(R.strings.full_name),
            key: 'full_name',
            className: 'patient-name-column',
            render: (_, record: IGetExportBill) => {
              return (
                <RenderValueTable
                  value={
                    record?.paymentDetails
                      ? record?.paymentDetails[0]?.patientName
                      : '-'
                  }
                  type="OtherLink"
                  hrefOtherLink={getDetailPatientUrl(
                    record?.paymentDetails
                      ? record?.paymentDetails[0]?.patientId
                      : '',
                  )}
                  styleText={{ fontSize: 12 }}
                />
              )
            },
          }),

          convertColumnTable<IDataTable>({
            title: t(R.strings._patient_code),
            key: '_patient_code',
            className: 'patient-code-column',
            render: (_, record: IGetExportBill) => {
              return (
                <TableText
                  children={
                    record?.paymentDetails
                      ? record?.paymentDetails[0]?.patientCode
                      : '-'
                  }
                  fontWeight="medium"
                  opacity="0.9"
                  textAlign="center"
                />
              )
            },
          }),
          convertColumnTable<IDataTable>({
            title: t(R.strings.branch),
            key: 'branch',
            className: 'branch-column',
            render: (_, record: IGetExportBill) => {
              return (
                <BaseCol>
                  {uniqBy(record?.paymentDetails, 'clinicName')?.map(
                    (i, index) => (
                      <ListItemText
                        $marginTop={index !== 0 ? 8 : 0}
                        key={`${index}`}
                        children={`${i?.clinicName}`}
                        fontWeight="medium"
                        opacity="0.9"
                      />
                    ),
                  )}
                </BaseCol>
              )
            },
          }),
          convertColumnTable<IDataTable>({
            title: t(R.strings.company_by_branch),
            key: 'company_by_branch',
            className: 'company-by-branch-column',
            render: (_, record: IGetExportBill) => {
              return (
                <BaseCol>
                  {uniqBy(record?.paymentDetails, 'clinicLegalName')?.map(
                    (i, index) => (
                      <ListItemText
                        $marginTop={index !== 0 ? 8 : 0}
                        key={`${index}`}
                        children={`${i?.clinicLegalName}`}
                        fontWeight="medium"
                        opacity="0.9"
                      />
                    ),
                  )}
                </BaseCol>
              )
            },
          }),
          convertColumnTable<IDataTable>({
            title: t(R.strings.address),
            key: 'address',
            className: 'address-column',
            render: (_, record: IGetExportBill) => {
              return (
                <BaseCol>
                  {uniqBy(record?.paymentDetails, 'patientAddress')?.map(
                    (i, index) => (
                      <ListItemText
                        $marginTop={index !== 0 ? 8 : 0}
                        key={`${index}`}
                        children={`${i?.patientAddress}`}
                        fontWeight="medium"
                        opacity="0.9"
                      />
                    ),
                  )}
                </BaseCol>
              )
            },
          }),
        ],
      },
      {
        title: t(R.strings.export_bill_info),
        children: [
          convertColumnTable<IDataTable>({
            title: t(R.strings.service_use),
            key: 'service_use',
            className: 'service-used-column',
            render: (_, record: IGetExportBill) => {
              return (
                <BaseCol>
                  {record?.usedServices
                    ?.filter(itm => isEqual(itm?.ttype, 'service'))
                    ?.map((i, index) => (
                      <ListItemText
                        $marginTop={index !== 0 ? 8 : 0}
                        key={`${index}`}
                        children={`- ${i?.name}`}
                        fontWeight="medium"
                        opacity="0.9"
                      />
                    ))}
                </BaseCol>
              )
            },
          }),
          convertColumnTable<IDataTable>({
            title: t(R.strings.service_use_date),
            key: 'service_use_date',
            className: 'date-column',
            render: (_, record: IGetExportBill) => {
              const createdAtList = uniq(
                record?.paymentDetails?.map(i => i?.createdAt),
              )
              return (
                <BaseCol>
                  {createdAtList?.map((i, index) => (
                    <ListItemText
                      $marginTop={index !== 0 ? 8 : 0}
                      key={`${index}`}
                      children={i ? DateUtil.formatDDMMYYY(i) : ''}
                      fontWeight="medium"
                      opacity="0.9"
                      textAlign="center"
                    />
                  ))}
                </BaseCol>
              )
            },
          }),
          convertColumnTable<IDataTable>({
            title: t(R.strings.total_amount_receivable),
            key: 'total_amount_receivable',
            className: 'money-column',
            render: (_, record: IGetExportBill) => {
              const price =
                record?.usedServices?.reduce(
                  (sum, item) => (item?.total ? sum + item?.total : sum),
                  0,
                ) || 0
              return (
                <TableText
                  children={price > 0 ? formatMoney(price, 'đ') : '0đ'}
                  fontWeight="medium"
                  opacity="0.9"
                  textAlign="right"
                />
              )
            },
          }),
          convertColumnTable<IDataTable>({
            title: t(R.strings.identity_number),
            key: 'identity_number',
            classNameWidthColumnOverwrite: 'normal-column',
            render: (_, record: IGetExportBill) => {
              return (
                <TableText
                  children={
                    record.targetType !== 'company' && record?.identityNumber
                      ? record?.identityNumber?.trim()
                      : '-'
                  }
                  fontWeight="medium"
                  opacity="0.9"
                  textAlign="center"
                />
              )
            },
          }),
          convertColumnTable<IDataTable>({
            title: t(R.strings.tax_code),
            key: 'tax_code',
            className: 'tax-code-column',
            render: (_, record: IGetExportBill) => {
              return (
                <TableText
                  children={record?.taxId}
                  fontWeight="medium"
                  opacity="0.9"
                  textAlign="center"
                />
              )
            },
          }),
          convertColumnTable<IDataTable>({
            title: t(R.strings.company_name_customer_name),
            key: 'company_name_customer_name',
            className: 'company-name-column',
            render: (_, record: IGetExportBill) => {
              return (
                <TableText
                  children={record?.companyName}
                  fontWeight="medium"
                  opacity="0.9"
                />
              )
            },
          }),
          convertColumnTable<IDataTable>({
            title: t(R.strings.address),
            key: 'address_customer',
            className: 'address-column',
            render: (_, record: IGetExportBill) => {
              return (
                <TableText
                  children={record?.address}
                  fontWeight="medium"
                  opacity="0.9"
                />
              )
            },
          }),
          convertColumnTable<IDataTable>({
            title: t(R.strings.email_receive_bill),
            key: 'email_receive_bill',
            className: 'email-column',
            render: (_, record: IGetExportBill) => {
              return (
                <TableText
                  children={record?.email}
                  fontWeight="medium"
                  opacity="0.9"
                />
              )
            },
          }),
          convertColumnTable<IDataTable>({
            title: t(R.strings.service_request_export_bill),
            key: 'service_request_export_bill',
            className: 'service-used-column',
            render: (_, record: IGetExportBill) => {
              return (
                <BaseCol>
                  {record?.selectedServices?.map((i, index) => (
                    <TableText
                      key={`${index}`}
                      children={`- ${i?.name}`}
                      fontWeight="medium"
                      opacity="0.9"
                    />
                  ))}
                </BaseCol>
              )
            },
          }),
          convertColumnTable<IDataTable>({
            title: t(R.strings.total_amount_export_bill),
            key: 'total_amount_export_bill',
            className: 'money-column',
            render: (_, record: IGetExportBill) => {
              const price =
                record?.selectedServices?.reduce(
                  (sum, item) => (item?.price ? sum + item?.price : sum),
                  0,
                ) || 0
              return (
                <TableText
                  children={price > 0 ? formatMoney(price, 'đ') : '0đ'}
                  fontWeight="medium"
                  opacity="0.9"
                  textAlign="right"
                />
              )
            },
          }),
        ],
      },
      {
        title: t(R.strings.status_export_bill),
        children: [
          convertColumnTable<IDataTable>({
            title: t(R.strings.exported_bill),
            key: 'exported_bill',
            classNameWidthColumnOverwrite: 'medium-column',
            render: (_, record: IGetExportBill) => {
              return (
                <CheckBoxContainer>
                  <CheckBox
                    checked={record?.isInvoiceExported}
                    onChange={() =>
                      updateCheckBox({
                        id: record?._id || '',
                        body: {
                          isInvoiceExported: !record?.isInvoiceExported,
                        },
                      })
                    }
                    disabled={!editPermission}
                  />
                </CheckBoxContainer>
              )
            },
          }),
          convertColumnTable<IDataTable>({
            title: t(R.strings.sent_mail),
            key: 'sent_mail',
            className: 'sent-mail-column',
            render: (_, record: IGetExportBill) => {
              return (
                <CheckBoxContainer>
                  <CheckBox
                    checked={record?.isMailSent}
                    onChange={() =>
                      updateCheckBox({
                        id: record?._id || '',
                        body: {
                          isMailSent: !record?.isMailSent,
                        },
                      })
                    }
                    disabled={!editPermission}
                  />
                </CheckBoxContainer>
              )
            },
          }),
        ],
      },
    ]
    return option
  }, [t])

  const handleTableChange = (pagination: Pagination) => {
    setPagination(pagination)
  }

  const handleChangeFilter = (newFilter: IFilterGetExportBill) => {
    setFilter(newFilter)
    setPagination({ ...pagination, current: 1 })
  }

  useEffect(() => {
    if (reloadTable > 0) {
      refetchData()
    }
  }, [reloadTable])

  return {
    data,
    columns,
    filter,
    isLoading,
    setIsLoading,
    isLoadingTable,
    handleTableChange,
    pagination: {
      ...pagination,
      total: dataFlatten?.total ?? 0,
    },
    handleChangeFilter,
    totalItem,
  }
}
